import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { unstable_useId as useId, unstable_capitalize as capitalize } from "@mui/utils";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import { gridFilterableColumnDefinitionsSelector } from "@mui/x-data-grid-premium";
import { gridFilterModelSelector } from "@mui/x-data-grid-premium";
import { useGridSelector } from "@mui/x-data-grid-premium";
import { GridLinkOperator } from "@mui/x-data-grid-premium";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import { useGridRootProps } from "@mui/x-data-grid-premium";
import BryxDropdown from "../BryxDropdown";
import BryxColors from "../../types/theme";
import BryxBox from "../BryxBox";
import { forwardRef, useRef, useMemo, useCallback, useImperativeHandle } from "react";
const GridFilterFormRoot = styled("div", {
    name: "MuiDataGrid",
    slot: "FilterForm",
    overridesResolver: (props, styles) => styles.filterForm,
})(({ theme }) => ({
    display: "flex",
    padding: theme.spacing(1),
    gap: "16px",
}));
const getColumnLabel = (col) => col.headerName || col.field;
const collator = new Intl.Collator();
export const BryxDataGridFilterPanelForm = forwardRef(function GridFilterForm(props, ref) {
    const { item, hasMultipleFilters, deleteFilter, applyFilterChanges, multiFilterOperator, showMultiFilterOperators, disableMultiFilterOperator, applyMultiFilterOperatorChanges, focusElementRef, linkOperators = [GridLinkOperator.And], columnsSort, filterColumns, deleteIconProps = {}, linkOperatorInputProps = {}, operatorInputProps = {}, columnInputProps = {}, valueInputProps = {}, children, ...other } = props;
    const apiRef = useGridApiContext();
    const filterableColumns = useGridSelector(apiRef, gridFilterableColumnDefinitionsSelector);
    const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
    const columnSelectId = useId();
    const columnSelectLabelId = useId();
    const operatorSelectId = useId();
    const operatorSelectLabelId = useId();
    const rootProps = useGridRootProps();
    const ownerState = { classes: rootProps.classes };
    const valueRef = useRef(null);
    const filterSelectorRef = useRef(null);
    const { InputComponentProps, ...valueInputPropsOther } = valueInputProps;
    const filteredColumns = useMemo(() => {
        if (filterColumns === undefined || typeof filterColumns !== "function") {
            return filterableColumns;
        }
        const filteredFields = filterColumns({
            field: item.columnField,
            columns: filterableColumns,
            currentFilters: filterModel?.items || [],
        });
        return filterableColumns.filter((column) => filteredFields.includes(column.field));
    }, [filterColumns, filterModel?.items, filterableColumns, item.columnField]);
    const sortedFilteredColumns = useMemo(() => {
        switch (columnsSort) {
            case "asc":
                return filteredColumns.sort((a, b) => collator.compare(getColumnLabel(a), getColumnLabel(b)));
            case "desc":
                return filteredColumns.sort((a, b) => -collator.compare(getColumnLabel(a), getColumnLabel(b)));
            default:
                return filteredColumns;
        }
    }, [filteredColumns, columnsSort]);
    const currentColumn = item.columnField ? apiRef.current.getColumn(item.columnField) : null;
    const currentOperator = useMemo(() => {
        if (!item.operatorValue || !currentColumn) {
            return null;
        }
        return currentColumn.filterOperators?.find((operator) => operator.value === item.operatorValue);
    }, [item, currentColumn]);
    const changeColumn = useCallback((event) => {
        const field = event.target.value;
        const column = apiRef.current.getColumn(field);
        if (currentColumn != null && column.field === currentColumn.field) {
            // column did not change
            return;
        }
        // try to keep the same operator when column change
        const newOperator = column.filterOperators.find((operator) => operator.value === item.operatorValue) || column.filterOperators[0];
        // Erase filter value if the input component is modified
        const eraseItemValue = !newOperator.InputComponent || newOperator.InputComponent !== currentOperator?.InputComponent;
        applyFilterChanges({
            ...item,
            columnField: field,
            operatorValue: newOperator.value,
            value: eraseItemValue ? undefined : item.value,
        });
    }, [apiRef, applyFilterChanges, item, currentColumn, currentOperator]);
    const changeOperator = useCallback((event) => {
        const operator = event.target.value;
        const newOperator = currentColumn?.filterOperators.find((op) => op.value === operator);
        const eraseItemValue = !newOperator?.InputComponent || newOperator?.InputComponent !== currentOperator?.InputComponent;
        applyFilterChanges({
            ...item,
            operatorValue: operator,
            value: eraseItemValue ? undefined : item.value,
        });
    }, [applyFilterChanges, item, currentColumn, currentOperator]);
    const handleDeleteFilter = () => {
        if (rootProps.disableMultipleColumnsFiltering) {
            if (item.value === undefined) {
                deleteFilter(item);
            }
            else {
                // TODO v6: simplify the behavior by always remove the filter form
                applyFilterChanges({ ...item, value: undefined });
            }
        }
        else {
            deleteFilter(item);
        }
    };
    useImperativeHandle(focusElementRef, () => ({
        focus: () => {
            if (currentOperator?.InputComponent) {
                valueRef?.current?.focus();
            }
            else {
                filterSelectorRef.current?.focus();
            }
        },
    }), [currentOperator]);
    return (_jsxs(GridFilterFormRoot, { ref: ref, className: "filterForm", "data-id": item.id, ...other, children: [_jsx(BryxDropdown, { labelId: columnSelectLabelId, id: columnSelectId, label: apiRef.current.getLocaleText("filterPanelColumns"), value: item.columnField || "", onChange: changeColumn, disablePortal: true, disableUnselect: true, short: true, sx: { "& .MuiSelect-select": { border: "none !important" } }, children: sortedFilteredColumns.map((col) => (_jsx(MenuItem, { value: col.field, children: getColumnLabel(col) }, col.field))) }), item.columnField != "" && (_jsxs(BryxBox, { isFlex: true, smGap: true, children: [_jsx(BryxDropdown, { labelId: operatorSelectLabelId, label: apiRef.current.getLocaleText("filterPanelOperators"), id: operatorSelectId, value: item.operatorValue, onChange: changeOperator, inputRef: filterSelectorRef, ...rootProps.componentsProps?.baseSelect, disablePortal: true, disableUnselect: true, short: true, sx: { "& .MuiSelect-select": { border: "none !important" } }, children: currentColumn?.filterOperators?.map((operator) => (_jsx(MenuItem, { value: operator.value, children: operator.label ||
                                apiRef.current.getLocaleText(`filterOperator${capitalize(operator.value)}`) }, operator.value))) }), currentOperator?.InputComponent ? (_jsx(currentOperator.InputComponent, { label: "Value", placeholder: "ex. Fire", apiRef: apiRef, item: item, applyValue: applyFilterChanges, focusElementRef: valueRef, ...currentOperator.InputComponentProps, ...InputComponentProps })) : null, _jsx(IconButton, { "aria-label": apiRef.current.getLocaleText("filterPanelDeleteIconLabel"), title: apiRef.current.getLocaleText("filterPanelDeleteIconLabel"), onClick: handleDeleteFilter, size: "small", sx: {
                            margin: "auto 0",
                            "&:hover": {
                                backgroundColor: "unset !important",
                            },
                            "& svg": {
                                marginTop: "16px",
                                fill: BryxColors.lightBrand(1),
                            },
                        }, children: _jsx(rootProps.components.FilterPanelDeleteIcon, { fontSize: "small" }) })] }))] }));
});
export default BryxDataGridFilterPanelForm;
